"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { pushGtmEvent } from "@/utils/utils";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { useDispatch } from "react-redux";
import {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  icon?: string;
  icon_alt?: string;
  text_color?: string;
  children?: MenuItem[];
}

interface RouteItem {
  id: number;
  route: string;
  label: string;
  textColor?: string;
  nestedRoutes?: {
    id: number;
    label: string;
    route: string;
    textColor?: string;
    icon?: string;
    iconAlt?: string;
  }[];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

const isExternal = (href: string) =>
  /^https?:\/\//i.test(href) || href.startsWith("//") || href.startsWith("tel:") || href.startsWith("mailto:");

/** Trailing slashes are cosmetic: "/a/" and "/a" are the same page. Root stays "/". */
const stripTrailingSlash = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;

/**
 * CMS authors write slugs both ways ("about" and "/about"), so prefixing blindly
 * yields "//about" — which the browser reads as a protocol-relative URL, not a path.
 * Collapse to exactly one leading slash; leave external/anchor hrefs untouched.
 */
const normalizePath = (raw?: string): string => {
  const v = (raw ?? "").trim();
  if (!v) return "";
  if (isExternal(v) || v.startsWith("#")) return v;
  return `/${v.replace(/^\/+/, "")}`;
};

/** `url` wins over `slug`; "" means the item has no link of its own. */
const resolvePath = (item: { url?: string; slug?: string }): string =>
  normalizePath(item.url) || normalizePath(item.slug);

/** Dropdown parents commonly use "" or "/" as a placeholder — the real links live in the children. */
const isPlaceholderPath = (path: string) => path === "" || path === "/";

/** Phone CTA: pass through real URLs; otherwise turn a bare number into `tel:`. */
const buildTelHref = (raw?: string) => {
  if (!raw) return undefined;
  const v = raw.trim();
  if (/^(https?:\/\/|\/\/|mailto:)/i.test(v)) return v;
  const digits = v.replace(/[^\d+]/g, ""); // keep digits and leading +
  return digits ? `tel:${digits}` : undefined;
};

/** WhatsApp CTA: pass through real URLs; otherwise turn a bare number into wa.me. */
const buildWhatsAppHref = (raw?: string) => {
  if (!raw) return undefined;
  const v = raw.trim();
  if (/^(https?:\/\/|whatsapp:|\/\/)/i.test(v)) return v;
  const digits = v.replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : undefined;
};

/* ------------------------------------------------------------------ */
/* Default inline-SVG icons (used when no CMS icon uploaded)           */
/* ------------------------------------------------------------------ */

/**
 * Logo box sizes, chosen per breakpoint from the CMS ("small" | "medium" |
 * "large"). The logo is `object-contain` inside this box, so the BOX is what
 * decides how large the artwork actually draws — and its aspect ratio decides
 * which axis constrains it.
 *
 * That is the whole reason this control exists. "small" is a 2.32:1 box, but a
 * university lockup (crest + wordmark + accreditation line) is nearer 4.5:1, so
 * contain fits it to the box WIDTH and it lands at barely half the box height —
 * the logo reads as tiny on wide screens no matter how large the upload is.
 *
 * "large" is taken from Figma F4PPlp4w7dQStkh31lzKJ6 node 10:6253: 258.5 ×
 * 57.44px at the 1430 frame, i.e. a 4.5:1 box that lets a full lockup fill its
 * height. sm/md are that same box scaled by breakpoint width (1080/1430,
 * 1230/1430). "medium" sits between the two for logos that are wide but not
 * that wide.
 *
 * "small" is byte-for-byte the chain this bar shipped with, which is why it is
 * the default — every existing navbar renders exactly as it does today.
 *
 * Mobile covers the base breakpoint; desktop covers sm (1080) and up. Mobile
 * steps up more gently: the bar there also holds the hamburger and CTAs, so a
 * full 4.5:1 box at large would crowd them off the row.
 */
const LOGO_SIZE_MOBILE: Record<string, string> = {
  small: "h-8 w-[74px]",
  medium: "h-10 w-[110px]",
  large: "h-12 w-[145px]",
};

const LOGO_SIZE_DESKTOP: Record<string, string> = {
  small: "sm:h-[30px] sm:w-[70px] md:h-10 md:w-[93px] lg:h-[50px] lg:w-[116px]",
  medium: "sm:h-9 sm:w-[132px] md:h-11 md:w-[157px] lg:h-[54px] lg:w-[187px]",
  large:
    "sm:h-[43px] sm:w-[195px] md:h-[49px] md:w-[222px] lg:h-[57.44px] lg:w-[258.5px]",
};

/** Unknown / empty CMS values fall back to the historical "small" box. */
const logoSizeClass = (map: Record<string, string>, value: unknown) =>
  map[String(value ?? "").trim().toLowerCase()] ?? map.small;

const PhoneIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-5"} viewBox="0 0 14 14" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M6.44616 8.72092L5.36699 9.80008C5.13949 10.0276 4.77783 10.0276 4.54449 9.80591C4.48033 9.74175 4.41616 9.68342 4.35199 9.61925C3.75116 9.01258 3.20866 8.37675 2.72449 7.71175C2.24616 7.04675 1.86116 6.38175 1.58116 5.72258C1.30699 5.05758 1.16699 4.42175 1.16699 3.81508C1.16699 3.41841 1.23699 3.03925 1.37699 2.68925C1.51699 2.33341 1.73866 2.00675 2.04783 1.71508C2.42116 1.34758 2.82949 1.16675 3.26116 1.16675C3.42449 1.16675 3.58783 1.20175 3.73366 1.27175C3.88533 1.34175 4.01949 1.44675 4.12449 1.59841L5.47783 3.50591C5.58283 3.65175 5.65866 3.78591 5.71116 3.91425C5.76366 4.03675 5.79283 4.15925 5.79283 4.27008C5.79283 4.41008 5.75199 4.55008 5.67033 4.68425C5.59449 4.81841 5.48366 4.95841 5.34366 5.09841L4.90033 5.55925C4.83616 5.62341 4.80699 5.69925 4.80699 5.79258C4.80699 5.83925 4.81283 5.88008 4.82449 5.92675C4.84199 5.97341 4.85949 6.00841 4.87116 6.04342C4.97616 6.23591 5.15699 6.48675 5.41366 6.79008C5.67616 7.09342 5.95616 7.40258 6.25949 7.71175C6.31783 7.77008 6.38199 7.82842 6.44033 7.88675C6.67366 8.11425 6.67949 8.48758 6.44616 8.72092Z" fill={color}/>
    <path d="M12.8154 10.6924C12.8154 10.8558 12.7863 11.0249 12.7279 11.1883C12.7104 11.2349 12.6929 11.2816 12.6696 11.3283C12.5704 11.5383 12.4421 11.7366 12.2729 11.9233C11.9871 12.2383 11.6721 12.4658 11.3163 12.6116C11.3104 12.6116 11.3046 12.6174 11.2988 12.6174C10.9546 12.7574 10.5813 12.8333 10.1788 12.8333C9.58378 12.8333 8.94794 12.6933 8.27711 12.4074C7.60628 12.1216 6.93544 11.7366 6.27044 11.2524C6.04294 11.0833 5.81544 10.9141 5.59961 10.7333L7.50711 8.82577C7.67044 8.94827 7.81628 9.04161 7.93878 9.10577C7.96794 9.11744 8.00294 9.13494 8.04378 9.15244C8.09044 9.16994 8.13711 9.17577 8.18961 9.17577C8.28878 9.17577 8.36461 9.14078 8.42878 9.07661L8.87211 8.63911C9.01794 8.49328 9.15794 8.38244 9.29211 8.31244C9.42628 8.23077 9.56044 8.18994 9.70628 8.18994C9.81711 8.18994 9.93378 8.21327 10.0621 8.26577C10.1904 8.31827 10.3246 8.39411 10.4704 8.49328L12.4013 9.86411C12.5529 9.96911 12.6579 10.0916 12.7221 10.2374C12.7804 10.3833 12.8154 10.5291 12.8154 10.6924Z" fill={color}/>
  </svg>
);

const WhatsAppIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-5"} viewBox="0 0 14 14" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M12.8217 6.65593C12.6233 3.27259 9.54917 0.6651 6.00834 1.24843C3.57 1.65093 1.61584 3.62842 1.23667 6.06675C1.01501 7.47842 1.30669 8.81426 1.94252 9.91676L1.42335 11.8476C1.30668 12.2851 1.70917 12.6818 2.14083 12.5593L4.04251 12.0343C4.90584 12.5418 5.915 12.8334 6.99417 12.8334C10.2842 12.8334 13.0142 9.93426 12.8217 6.65593ZM9.84668 9.17009C9.79418 9.27509 9.73 9.37426 9.64833 9.4676C9.5025 9.6251 9.34499 9.74176 9.16999 9.81176C8.99499 9.8876 8.80251 9.9226 8.59834 9.9226C8.30084 9.9226 7.98 9.8526 7.6475 9.70677C7.30917 9.56093 6.97669 9.36842 6.64419 9.12926C6.30585 8.88425 5.99084 8.61009 5.68751 8.31259C5.38418 8.00926 5.11583 7.68842 4.87083 7.35592C4.63166 7.02342 4.43917 6.69092 4.29917 6.35842C4.15917 6.02592 4.08918 5.7051 4.08918 5.40176C4.08918 5.20343 4.12418 5.01093 4.19418 4.83593C4.26418 4.6551 4.37502 4.49177 4.53252 4.34593C4.71919 4.15927 4.92334 4.07176 5.13918 4.07176C5.22084 4.07176 5.30249 4.08926 5.37833 4.12426C5.45416 4.15926 5.52418 4.21176 5.57668 4.2876L6.25333 5.24425C6.30583 5.32009 6.34667 5.38426 6.37 5.44842C6.39917 5.51259 6.41084 5.57092 6.41084 5.62925C6.41084 5.69925 6.38751 5.76926 6.34668 5.83926C6.30585 5.90926 6.25333 5.97926 6.18333 6.04926L5.96166 6.28259C5.92666 6.31759 5.91502 6.35259 5.91502 6.39926C5.91502 6.42259 5.92083 6.44592 5.92666 6.46926C5.93833 6.49259 5.94418 6.51009 5.95002 6.52759C6.00252 6.62676 6.09583 6.74925 6.22417 6.90092C6.35833 7.05258 6.49836 7.21009 6.65002 7.36176C6.80752 7.51926 6.95918 7.65925 7.11668 7.79342C7.26835 7.92175 7.39666 8.00926 7.49583 8.06176C7.51333 8.0676 7.53085 8.07925 7.54835 8.08508C7.57168 8.09675 7.59502 8.09676 7.62418 8.09676C7.67668 8.09676 7.71169 8.07926 7.74669 8.04426L7.96833 7.82259C8.04416 7.74676 8.11419 7.69426 8.17836 7.65926C8.24836 7.61843 8.31251 7.59509 8.38835 7.59509C8.44668 7.59509 8.50501 7.60676 8.56918 7.63592C8.63335 7.66509 8.70333 7.7001 8.77333 7.7526L9.74168 8.44093C9.81752 8.49343 9.87 8.5576 9.905 8.6276C9.93417 8.70343 9.95168 8.77342 9.95168 8.85509C9.91668 8.95425 9.89335 9.06509 9.84668 9.17009Z" fill={color}/>
  </svg>
);

const ApplyArrowIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-5"} viewBox="0 0 14 14" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M7.00033 1.16675C3.78033 1.16675 1.16699 3.78008 1.16699 7.00008C1.16699 10.2201 3.78033 12.8334 7.00033 12.8334C10.2203 12.8334 12.8337 10.2201 12.8337 7.00008" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.58301 6.41663L12.3663 1.6333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.8331 3.98425V1.16675H10.0156" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="8"
    viewBox="0 0 10 6"
    fill="none"
    className={cn("transition-transform duration-200", open && "rotate-180")}
    aria-hidden
  >
    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HamburgerIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M21 5.25H3C2.59 5.25 2.25 4.91 2.25 4.5C2.25 4.09 2.59 3.75 3 3.75H21C21.41 3.75 21.75 4.09 21.75 4.5C21.75 4.91 21.41 5.25 21 5.25Z" fill={color}/>
    <path d="M21 10.25H3C2.59 10.25 2.25 9.91 2.25 9.5C2.25 9.09 2.59 8.75 3 8.75H21C21.41 8.75 21.75 9.09 21.75 9.5C21.75 9.91 21.41 10.25 21 10.25Z" fill={color}/>
    <path d="M21 15.25H3C2.59 15.25 2.25 14.91 2.25 14.5C2.25 14.09 2.59 13.75 3 13.75H21C21.41 13.75 21.75 14.09 21.75 14.5C21.75 14.91 21.41 15.25 21 15.25Z" fill={color}/>
    <path d="M21 20.25H3C2.59 20.25 2.25 19.91 2.25 19.5C2.25 19.09 2.59 18.75 3 18.75H21C21.41 18.75 21.75 19.09 21.75 19.5C21.75 19.91 21.41 20.25 21 20.25Z" fill={color}/>
  </svg>
);

const CloseIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM15.36 14.3C15.65 14.59 15.65 15.07 15.36 15.36C15.21 15.51 15.02 15.58 14.83 15.58C14.64 15.58 14.45 15.51 14.3 15.36L12 13.06L9.7 15.36C9.55 15.51 9.36 15.58 9.17 15.58C8.98 15.58 8.79 15.51 8.64 15.36C8.35 15.07 8.35 14.59 8.64 14.3L10.94 12L8.64 9.7C8.35 9.41 8.35 8.93 8.64 8.64C8.93 8.35 9.41 8.35 9.7 8.64L12 10.94L14.3 8.64C14.59 8.35 15.07 8.35 15.36 8.64C15.65 8.93 15.65 9.41 15.36 9.7L13.06 12L15.36 14.3Z" fill={color}/>
  </svg>
);

const DropdownItemIcon = ({ color, size = 14 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M14.5 9.5 7 17a3.54 3.54 0 0 1-5-5l7.5-7.5a2.36 2.36 0 0 1 3.34 3.34L7 13.69"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ------------------------------------------------------------------ */
/* CTA button (shared by phone / WhatsApp / Apply Now)                 */
/* ------------------------------------------------------------------ */

const CtaButton = ({
  label,
  icon,
  iconAlt,
  fallbackIcon,
  bgColor,
  textColor,
  borderColor,
  labelMode = "lg",
  iconRight = false,
  onClick,
  href,
}: {
  label?: string;
  icon?: string;
  iconAlt?: string;
  fallbackIcon: React.ReactNode;
  bgColor: string;
  textColor: string;
  borderColor?: string;
  /** "always" = label always visible (Apply Now); "lg" = icon-only until lg (phone/WhatsApp); "never" = always icon-only round. */
  labelMode?: "always" | "lg" | "never";
  /** Render the icon after the label (Apply Now). */
  iconRight?: boolean;
  onClick?: () => void;
  href?: string;
}) => {
  const style: CSSProperties = {
    backgroundColor: bgColor,
    color: textColor,
    ...(borderColor && borderColor !== "transparent"
      ? { border: `1px solid ${borderColor}` }
      : {}),
  };

  // Icon glyph size: bigger inside the always-round (icon-only) WhatsApp button.
  const iconClassName =
    labelMode === "never" ? "size-[18px] lg:size-7" : "size-[18px] sm:size-5";

  // The wrapper owns the responsive size; the icon (uploaded image or SVG fallback) fills it.
  const iconEl = (
    <span className={cn("flex shrink-0 items-center justify-center", iconClassName)}>
      {icon ? (
        <Image
          src={icon}
          alt={iconAlt || label || ""}
          width={28}
          height={28}
          className="size-full object-contain"
        />
      ) : (
        fallbackIcon
      )}
    </span>
  );

  const labelEl =
    label && labelMode !== "never" ? (
      <span
        className={cn(
          "whitespace-nowrap leading-tight",
          // weight: 600 everywhere, 500 only at sm (1080) per design
          "font-semibold sm:font-medium md:font-semibold",
          labelMode === "always"
            ? "text-[12px] sm:text-[14px] lg:text-[16px]"
            : "hidden text-[14px] sm:inline lg:text-[16px]",
        )}
      >
        {label}
      </span>
    ) : null;

  const content = iconRight ? (
    <>
      {labelEl}
      {iconEl}
    </>
  ) : (
    <>
      {iconEl}
      {labelEl}
    </>
  );

  const className = cn(
    "flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-full transition-opacity hover:opacity-90",
    labelMode === "always"
      ? "px-2.5 py-1.5 sm:px-4 sm:py-2"
      : labelMode === "never"
        ? // icon-only WhatsApp: 30px on mobile, 50px on large desktop
          "size-[30px] lg:size-[50px]"
        : // phone/WhatsApp: 30px round on mobile, pill once the label shows (sm+)
          "size-[30px] sm:h-auto sm:w-auto sm:px-4 sm:py-2",
  );

  if (href) {
    return (
      <a
        href={href}
        target={isExternal(href) && !href.startsWith("tel:") ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={className}
        style={style}
        aria-label={label || iconAlt}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className} style={style} aria-label={label || iconAlt}>
      {content}
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Announcement pill (CSS marquee only when text overflows)           */
/* ------------------------------------------------------------------ */

const AnnouncementPill = ({
  text,
  textColor,
  bgColor,
  borderColor,
  className,
}: {
  text: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  className?: string;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    const measure = () => {
      const vp = viewportRef.current;
      const ct = contentRef.current;
      if (!vp || !ct) return;
      setOverflow(ct.scrollWidth > vp.clientWidth + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text]);

  return (
    <div
      className={cn("rounded-[10px] p-1", className)}
      style={{ border: `1px solid ${borderColor}` }}
    >
      <div
        ref={viewportRef}
        className="overflow-hidden rounded-[8px] px-4 py-1.5 sm:py-1"
        style={{ backgroundColor: bgColor }}
      >
        <div className={cn("flex w-full", overflow ? "justify-start" : "justify-center")}>
          <span
            ref={contentRef}
            className={cn(
              "whitespace-nowrap text-[12px] font-medium leading-normal lg:text-[13px] xl:text-[14px]",
              overflow && "navbar3-marquee",
            )}
            style={{ color: textColor }}
          >
            {text}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* NavBar3 — combined sticky announcement bar + navigation            */
/* ------------------------------------------------------------------ */

const NavBar3 = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  const sectionData = data?.data ?? data;
  const isPreview = Boolean(data?.isPreview ?? false);
  const pathname = usePathname();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState<number | null>(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);

  const {
    // Announcement bar
    announcement_bar_bg_color = "#FFFFFF",
    announcement_text = "",
    announcement_text_color = "#9F1C34",
    announcement_bg_color = "#F2E0E3",
    announcement_border_color = "#9F1C34",
    logo_icon = "",
    logo_icon_alt = "Logo",
    logo_size_desktop = "small",
    logo_size_mobile = "small",
    // Nav row
    background_color = "#9F1C34",
    nav_text_color = "#FFFFFF",
    active_menu_color = "#F4BEBE",
    border_color = "rgba(0,0,0,0.2)",
    // Mobile-specific color overrides
    mobile_announcement_bg_color = "#9F1C34",
    mobile_announcement_text_color = "#FFFFFF",
    mobile_announcement_border_color = "#9F1C34",
    mobile_menu_text_color = "#000000",
    mobile_active_menu_color = "#9F1C34",
    // Dropdown
    dropdown_bg_color = "#F2E0E3",
    dropdown_text_color = "#000000",
    // Mobile chrome
    hamburger_icon = "",
    hamburger_icon_alt = "Menu",
    mobile_cancel_icon = "",
    mobile_cancel_icon_alt = "Close",
    menuItems = [],
  } = sectionData ?? {};

  const showDropdownIcons = toBool(sectionData?.show_dropdown_icons, true);
  /* Hamburger visibility. Defaults TRUE — a missing or NULL value must keep
     the hamburger, so only an explicit editor opt-out ever hides it. */
  const showHamburger = toBool(sectionData?.show_hamburger, true);

  /**
   * Logo → "/" redirect. NULL/undefined means "not configured" and stays ON, so
   * navbars saved before this toggle existed keep linking home; only an explicit
   * FALSE drops the anchor.
   */
  const logoRedirects = toBool(sectionData?.logo_redirect_enabled, true);

  /** Shared by both logo wrappers so the linked and unlinked forms are identical in size. */
  const logoBoxClass = cn(
    "relative block shrink-0",
    logoSizeClass(LOGO_SIZE_MOBILE, logo_size_mobile),
    logoSizeClass(LOGO_SIZE_DESKTOP, logo_size_desktop),
  );

  // Transform backend menu items → routes (active + sorted), memoized.
  const routes: RouteItem[] = useMemo(() => {
    if (!Array.isArray(menuItems) || menuItems.length === 0) return [];
    return menuItems
      .filter((item: MenuItem) => item.status)
      .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority)
      .map((item: MenuItem) => {
        const route: RouteItem = {
          id: item.id,
          route: resolvePath(item),
          label: item.title,
          textColor: item.text_color,
        };
        if (item.children && item.children.length > 0) {
          route.nestedRoutes = item.children
            .filter((c: MenuItem) => c.status)
            .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority)
            .map((c: MenuItem) => ({
              id: c.id,
              label: c.title,
              route: resolvePath(c),
              textColor: c.text_color,
              icon: c.icon,
              iconAlt: c.icon_alt,
            }));
        }
        return route;
      });
  }, [menuItems]);

  // All active menu items are nav links (NavBar3 has no button-type items;
  // its CTAs are the dedicated cta1/cta2/cta3 fields).
  const menuRoutes = routes;

  const currentPath = stripTrailingSlash(pathname || "/");

  /** A link is "current" only when it actually points at this page — never for external/anchor hrefs. */
  const isPathActive = (routePath: string) => {
    if (!routePath || routePath.startsWith("#") || isExternal(routePath)) {
      return false;
    }
    return stripTrailingSlash(routePath) === currentPath;
  };

  /**
   * Compared per item rather than against a single shared `activePath` string, so
   * items that happen to share a route (e.g. several link-less dropdowns) no longer
   * all highlight together. A link-less dropdown is active only via its children.
   */
  const isRouteActive = (route: RouteItem) => {
    const nested = route.nestedRoutes;
    if (nested && nested.length > 0) {
      if (!isPlaceholderPath(route.route) && isPathActive(route.route)) {
        return true;
      }
      return nested.some((n) => isPathActive(n.route));
    }
    return isPathActive(route.route);
  };

  // Close desktop dropdown on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDesktopDropdown(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // CTA config
  const cta1Enabled = toBool(sectionData?.cta1_enabled, true);
  const cta2Enabled = toBool(sectionData?.cta2_enabled, true);
  /**
   * CTA-3 (Apply Now) is optional: an empty label hides the button outright, so
   * there is deliberately NO "Apply Now" fallback here — a fallback would make it
   * impossible for an editor to turn the button off.
   */
  const cta3Label = String(sectionData?.cta3_label ?? "").trim();
  const cta3Enabled = cta3Label !== "";
  // Only when all three are actually on does the bar get crowded enough for
  // WhatsApp to collapse to its icon-only round form.
  const threeButtons = cta1Enabled && cta2Enabled && cta3Enabled;

  /** No nav links → nothing for the hamburger to open, so the drawer + toggle are dropped. */
  const hasMenuLinks = menuRoutes.length > 0;

  const cta1Href = buildTelHref(sectionData?.cta1_href || sectionData?.cta1_label);
  const cta2Href = buildWhatsAppHref(sectionData?.cta2_href);

  return (
    <header
      className={cn("w-full", isPreview ? "relative" : "sticky top-0 z-50")}
    >
      {/* keyframes for the announcement marquee (scoped, no external lib) */}
      <style>{`
        @keyframes navbar3-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        .navbar3-marquee {
          display: inline-block;
          padding-left: 100%;
          animation: navbar3-marquee 18s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .navbar3-marquee { animation: none; padding-left: 0; }
        }
      `}</style>

      {/* ---------------- Row 1: Announcement bar ---------------- */}
      <div
        className={cn(
          "flex w-full items-center gap-3 sm:gap-6 lg:gap-10",
          "px-6 py-3 sm:px-[60px] sm:py-3.5 lg:px-20 lg:py-5 xl:px-40",
        )}
        style={{
          backgroundColor: announcement_bar_bg_color,
          borderBottom: `1px solid ${border_color}`,
        }}
      >
        {/* Logo — links to "/" unless the redirect toggle is explicitly off.
            `relative` is load-bearing either way: the <Image fill> below needs a
            positioned ancestor, so the plain wrapper keeps the same classes. */}
        {logo_icon ? (
          logoRedirects ? (
            <Link href="/" className={logoBoxClass}>
              <Image
                src={logo_icon}
                alt={logo_icon_alt}
                fill
                priority
                className="object-contain"
              />
            </Link>
          ) : (
            <div className={logoBoxClass}>
              <Image
                src={logo_icon}
                alt={logo_icon_alt}
                fill
                priority
                className="object-contain"
              />
            </div>
          )
        ) : null}

        {/* Announcement pill — inline on desktop (hidden on mobile; mobile gets its own strip below) */}
        {announcement_text ? (
          <AnnouncementPill
            text={announcement_text}
            textColor={announcement_text_color}
            bgColor={announcement_bg_color}
            borderColor={announcement_border_color}
            className="hidden min-w-0 flex-1 sm:block"
          />
        ) : (
          <div className="hidden flex-1 sm:block" />
        )}

        {/* CTA group */}
        <div className="ml-auto flex shrink-0 items-center gap-2.5 sm:ml-0">
          {cta1Enabled ? (
            <CtaButton
              label={sectionData?.cta1_label}
              icon={sectionData?.cta1_icon}
              iconAlt={sectionData?.cta1_icon_alt}
              fallbackIcon={
                <PhoneIcon
                  color={sectionData?.cta1_text_color || "#000000"}
                  className="size-full"
                />
              }
              bgColor={sectionData?.cta1_bg_color || "transparent"}
              textColor={sectionData?.cta1_text_color || "#000000"}
              borderColor={sectionData?.cta1_border_color || "#000000"}
              labelMode="lg"
              href={cta1Href}
              onClick={() =>
                pushGtmEvent("ph_click", {
                  section: "navbar",
                  label: sectionData?.cta1_label || "phone",
                  link_url: cta1Href || "",
                })
              }
            />
          ) : null}

          {cta2Enabled ? (
            <CtaButton
              label={sectionData?.cta2_label}
              icon={sectionData?.cta2_icon}
              iconAlt={sectionData?.cta2_icon_alt}
              fallbackIcon={
                <WhatsAppIcon
                  color={sectionData?.cta2_text_color || "#FFFFFF"}
                  className="size-full"
                />
              }
              bgColor={sectionData?.cta2_bg_color || "#4CAF51"}
              textColor={sectionData?.cta2_text_color || "#FFFFFF"}
              borderColor={sectionData?.cta2_border_color}
              labelMode={threeButtons ? "never" : "lg"}
              href={cta2Href}
              onClick={() =>
                pushGtmEvent("wa_click", {
                  section: "navbar",
                  label: sectionData?.cta2_label || "whatsapp",
                  link_url: cta2Href || "",
                })
              }
            />
          ) : null}

          {/* CTA-3 Apply Now (optional — hidden when no label is configured) */}
          {cta3Enabled ? (
            <CtaButton
              label={cta3Label}
              icon={sectionData?.cta3_icon}
              iconAlt={sectionData?.cta3_icon_alt}
              fallbackIcon={
                <ApplyArrowIcon
                  color={sectionData?.cta3_text_color || "#FFFFFF"}
                  className="size-full"
                />
              }
              bgColor={sectionData?.cta3_bg_color || "#9F1C34"}
              textColor={sectionData?.cta3_text_color || "#FFFFFF"}
              labelMode="always"
              iconRight
              onClick={() =>
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  }),
                )
              }
            />
          ) : null}

          {/* Mobile hamburger / close — only when there are links to open AND
              the editor hasn't switched the hamburger off */}
          {hasMenuLinks && showHamburger ? (
            <button
              type="button"
              className="flex size-6 shrink-0 items-center justify-center sm:hidden"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                mobile_cancel_icon ? (
                  <Image
                    src={mobile_cancel_icon}
                    alt={mobile_cancel_icon_alt}
                    width={24}
                    height={24}
                  />
                ) : (
                  <CloseIcon color="#292D32" />
                )
              ) : hamburger_icon ? (
                <Image
                  src={hamburger_icon}
                  alt={hamburger_icon_alt}
                  width={24}
                  height={24}
                />
              ) : (
                <HamburgerIcon color="#292D32" />
              )}
            </button>
          ) : null}
        </div>
      </div>

      {/* ---------------- Mobile announcement strip (below header row) ----------------
          Transparent container; pill is maroon fill + white text (Figma 412:2432). */}
      {announcement_text ? (
        <div className="flex w-full justify-center px-6 py-3 sm:hidden">
          <AnnouncementPill
            text={announcement_text}
            textColor={mobile_announcement_text_color}
            bgColor={mobile_announcement_bg_color}
            borderColor={mobile_announcement_border_color}
            className="block w-full"
          />
        </div>
      ) : null}

      {/* ---------------- Row 2: Navigation (desktop) ---------------- */}
      <nav
        ref={dropdownRef}
        className={cn(
          "hidden w-full items-center justify-center sm:flex",
          "gap-x-[30px] md:gap-x-[40px] lg:gap-x-[54px] xl:gap-x-[70px]",
          "px-[60px] py-3.5 lg:px-20 xl:px-40",
        )}
        style={{
          backgroundColor: background_color,
          borderBottom: `1px solid ${border_color}`,
        }}
      >
        {menuRoutes.map((route) => {
          const isActive = isRouteActive(route);
          return route.nestedRoutes && route.nestedRoutes.length > 0 ? (
            <div key={route.id} className="relative">
              <button
                type="button"
                onClick={() =>
                  setOpenDesktopDropdown(
                    openDesktopDropdown === route.id ? null : route.id,
                  )
                }
                className={cn(
                  "flex items-center gap-1.5 border-b-2 border-transparent text-[14px] font-semibold leading-tight transition-opacity hover:opacity-80 lg:text-[16px]",
                  (openDesktopDropdown === route.id || isActive) &&
                    "border-current!",
                )}
                style={{
                  color:
                    openDesktopDropdown === route.id || isActive
                      ? active_menu_color
                      : route.textColor || nav_text_color,
                }}
                aria-expanded={openDesktopDropdown === route.id}
              >
                {route.label}
                <ChevronIcon open={openDesktopDropdown === route.id} />
              </button>
              {openDesktopDropdown === route.id ? (
                <div
                  className="absolute left-0 top-full z-50 mt-2.5 flex w-max min-w-[260px] max-w-[380px] flex-col gap-1 rounded-[16px] px-5 py-3.5 drop-shadow-[0px_4px_15px_rgba(0,0,0,0.25)]"
                  style={{ backgroundColor: dropdown_bg_color }}
                >
                  {route.nestedRoutes.map((nested) => (
                    <Link
                      key={nested.id}
                      href={nested.route || "#"}
                      onClick={() => setOpenDesktopDropdown(null)}
                      className="flex w-full items-center gap-2.5 py-1 text-[15px] font-normal leading-[1.55] tracking-[0.075px] transition-opacity hover:opacity-80"
                      style={{ color: dropdown_text_color }}
                    >
                      {showDropdownIcons ? (
                        nested.icon ? (
                          <Image
                            src={nested.icon}
                            alt={nested.iconAlt || ""}
                            width={20}
                            height={20}
                            className="shrink-0 object-contain"
                          />
                        ) : (
                          <DropdownItemIcon
                            color={dropdown_text_color}
                            size={20}
                          />
                        )
                      ) : null}
                      {nested.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <Link
              key={route.id}
              href={route.route || "#"}
              className={cn(
                "border-b-2 border-transparent text-[14px] font-semibold leading-tight transition-opacity hover:opacity-80 lg:text-[16px]",
                isActive && "border-current!",
              )}
              style={{
                color: isActive
                  ? active_menu_color
                  : route.textColor || nav_text_color,
              }}
            >
              {route.label}
            </Link>
          );
        })}
      </nav>

      {/* ---------------- Mobile drawer ---------------- */}
      {hasMenuLinks && isMobileMenuOpen ? (
        <div
          className="absolute left-0 right-0 top-full z-50 flex max-h-[80vh] flex-col gap-2 overflow-y-auto px-5 py-3.5 shadow-lg sm:hidden"
          style={{
            backgroundColor: announcement_bar_bg_color,
            borderBottom: `1px solid ${border_color}`,
          }}
        >
          {menuRoutes.map((route) => {
            const isActive = isRouteActive(route);
            return route.nestedRoutes && route.nestedRoutes.length > 0 ? (
              <div key={route.id} className="w-full">
                <button
                  type="button"
                  onClick={() =>
                    setOpenMobileAccordion(
                      openMobileAccordion === route.id ? null : route.id,
                    )
                  }
                  className={cn(
                    "flex w-full items-center justify-between py-1 text-[15px] leading-snug transition-opacity hover:opacity-80",
                    isActive ? "font-semibold" : "font-medium",
                  )}
                  style={{
                    color: isActive
                      ? mobile_active_menu_color
                      : mobile_menu_text_color,
                  }}
                >
                  {route.label}
                  <ChevronIcon open={openMobileAccordion === route.id} />
                </button>
                {openMobileAccordion === route.id ? (
                  <div className="flex flex-col gap-1 py-1 pl-2.5">
                    {route.nestedRoutes.map((nested) => (
                      <Link
                        key={nested.id}
                        href={nested.route || "#"}
                        onClick={() => {
                          setOpenMobileAccordion(null);
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-1.5 py-0.5 text-[13px] font-normal transition-opacity hover:opacity-80"
                        style={{ color: mobile_menu_text_color }}
                      >
                        {showDropdownIcons ? (
                          nested.icon ? (
                            <Image
                              src={nested.icon}
                              alt={nested.iconAlt || ""}
                              width={14}
                              height={14}
                              className="shrink-0 object-contain"
                            />
                          ) : (
                            <DropdownItemIcon color={mobile_menu_text_color} />
                          )
                        ) : null}
                        {nested.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <Link
                key={route.id}
                href={route.route || "#"}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  "py-1 text-[15px] leading-snug transition-opacity hover:opacity-80",
                  isActive ? "font-semibold" : "font-medium",
                )}
                style={{
                  color: isActive
                    ? mobile_active_menu_color
                    : mobile_menu_text_color,
                }}
              >
                {route.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </header>
  );
};

export default NavBar3;
